import { cacher } from '@/utils/rtkQueryCacheUtils'
import { Patient } from '../models/patient.model'
import { CreateReportDto, CreateReportTemplateDto, Report, ReportAnswer, ReportQuery, ReportTemplate, ReportTemplateQuery } from '../models/report.model'
import { baseApi, Tag } from './base'

export const ReportApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTemplates: builder.query<ReportTemplate[], ReportTemplateQuery>({
      query: ({ userId, isDefault }) => ({
        url: `/reports/templates?userId=${userId}${isDefault ? `&isDefault=${isDefault}` : ''}`,
        method: 'GET',
      }),
      providesTags: cacher.providesList(Tag.REPORT_TEMPLATE),
    }),
    createTemplate: builder.mutation<ReportTemplate, CreateReportTemplateDto>({
      query: (body) => ({
        url: '/reports/templates',
        method: 'POST',
        body,
      }),
      invalidatesTags: cacher.invalidatesList(Tag.REPORT_TEMPLATE),
    }),
    updateTemplate: builder.mutation<ReportTemplate, { id: string } & CreateReportTemplateDto>({
      query: ({ id, ...body }) => ({
        url: `/reports/templates/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: Tag.REPORT_TEMPLATE, id },
        { type: Tag.REPORT_TEMPLATE, id: 'LIST' },
      ],
    }),
    createReport: builder.mutation<Report, CreateReportDto>({
      query: (body) => ({ url: `/reports`, method: 'POST', body }),
      invalidatesTags: (result, error, arg) => [{ type: Tag.REPORT, id: 'LIST' }, ...(result ? [{ type: Tag.REPORT, id: result._id }] : [])],
    }),
    updateReport: builder.mutation<Report, { reportId: string; responses: ReportAnswer[]; lastUpdatedBy: string }>({
      query: ({ reportId, responses, lastUpdatedBy }) => ({
        url: `/reports/${reportId}`,
        method: 'PATCH',
        body: { responses, lastUpdatedBy },
      }),
      invalidatesTags: (result, error, { reportId }) => [
        { type: Tag.REPORT, id: reportId },
        { type: Tag.REPORT, id: 'LIST' },
      ],
    }),
    deleteReport: builder.mutation<Report, string>({
      query: (reportId) => ({
        url: `/reports/${reportId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, reportId) => [
        { type: Tag.REPORT, id: reportId },
        { type: Tag.REPORT, id: 'LIST' },
      ],
    }),
    regenerateReport: builder.mutation<Report, CreateReportDto>({
      query: ({ _id, lastUpdatedBy }) => ({ url: `/reports/${_id}/regenerate`, method: 'PATCH', body: { lastUpdatedBy } }),
      invalidatesTags: (result) => [
        { type: Tag.REPORT, id: result?._id },
        { type: Tag.REPORT, id: 'LIST' },
      ],
    }),
    getPatientHealthRecord: builder.query<Pick<Patient, 'vaccins' | 'background' | 'allergy' | 'treatment'>, string>({
      query: (reportId) => ({
        url: `/reports/${reportId}/patient-health-record`,
        method: 'GET',
      }),
    }),
    getReports: builder.query<Report[], ReportQuery>({
      query: ({ userId, transcriptionId }) => ({
        url: `/reports?${userId ? `userId=${userId}` : ''}${transcriptionId ? `&transcriptionId=${transcriptionId}` : ''}`,
        method: 'GET',
      }),
      providesTags: cacher.providesList(Tag.REPORT),
    }),
    getReportById: builder.query<Report, string>({
      query: (reportId) => ({
        url: `/reports/${reportId}`,
        method: 'GET',
      }),
      providesTags: (result) => (result ? [{ type: Tag.REPORT, id: result._id }] : []),
    }),
    deleteTemplate: builder.mutation<ReportTemplate, string>({
      query: (templateId) => ({
        url: `/reports/templates/${templateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, templateId) => [
        { type: Tag.REPORT_TEMPLATE, id: templateId },
        { type: Tag.REPORT_TEMPLATE, id: 'LIST' },
      ],
    }),
    updateWithTemplate: builder.mutation<Report, { template: string; lastUpdatedBy: string; reportId: string }>({
      query: ({ template, lastUpdatedBy, reportId }) => ({
        url: `/reports/${reportId}/update-with-template`,
        method: 'PATCH',
        body: { template, lastUpdatedBy },
      }),
      invalidatesTags: (result, error, { reportId }) => [
        { type: Tag.REPORT, id: reportId },
        { type: Tag.REPORT, id: 'LIST' },
      ],
    }),
  }),
})
